import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
// Removido: import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Upload, Trash2, Image as ImageIcon, Settings as SettingsIcon, Mail, Monitor, Wrench, Clock, AlertTriangle, Send, FileText } from 'lucide-react';
import { ContractTemplateEditor } from '@/components/ContractTemplateEditor';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '@/components/Layout';
import RoleBasedNavigation from '@/components/RoleBasedNavigation';

// Base da API. Em produção o frontend (dashboard.atomos.tech) e o backend
// (apiadmin.atomos.tech) ficam em hosts diferentes, e o cookie auth_token é
// escopado no host do backend. Por isso TODA chamada precisa ir para
// VITE_API_BASE_URL — usar caminho relativo '/api' faz a requisição cair na
// origem do frontend, que não recebe o cookie e retorna 401. Em same-origin
// (local) o fallback '/api' continua válido.
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

interface LoginImage {
  id: number;
  url: string;
  alt: string;
  order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const Settings = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newImageAlt, setNewImageAlt] = useState('');

  // Estados para configurações de alertas SMTP
  const [smtpSettings, setSmtpSettings] = useState({
    jobStoppedTimeSeconds: 60,
    alertEmails: ['rms.empreendimento@gmail.com'],
    enableLoteCreated: true,
    enableLoteStarted: true,
    enableLoteCompleted: true,
    enableLoteStopped: true,
    enableLotePaused: true,
    enableLoteResumed: true,
    enableAllAlerts: true
  });

  // Estado local para o campo de e-mails (permite digitação livre)
  const [emailInput, setEmailInput] = useState('rms.empreendimento@gmail.com');

  // Estados para configurações do dashboard
  const [dashboardSettings, setDashboardSettings] = useState({
    refreshIntervalSeconds: 60
  });

  // Estados para ferramentas de developer
  const [developerSettings, setDeveloperSettings] = useState({
    redisCleanupInterval: 'hourly', // hourly, daily, fixed_time
    redisCleanupTime: '02:00' // HH:MM format
  });

  // Estados para webhooks de lotes
  const [webhookSettings, setWebhookSettings] = useState({
    enabled: false,
    webhookUrl: '',
    enableLoteCreated: true,
    enableLoteStarted: true,
    enableLotePaused: true,
    enableLoteResumed: true,
    enableLoteCancelled: true,
    enableLoteCompleted: true,
    enableLoteStopped: true
  });

  // Estados para espelhamento de PDF V2 (ÁtomosChat)
  const [atomosV2MirrorSettings, setAtomosV2MirrorSettings] = useState({
    enabled: false,
  });
  const [atomosV2MirrorS3Info, setAtomosV2MirrorS3Info] = useState<{
    configured: boolean;
    endpoint: string | null;
    bucket: string | null;
    region: string | null;
  } | null>(null);

  // Estados de loading
  const [loading, setLoading] = useState({
    smtp: false,
    dashboard: false,
    developer: false,
    webhooks: false,
    atomosV2Mirror: false,
    actions: {
      pauseAll: false,
      resumeAll: false,
      clearRedis: false
    },
    webhookTestEvent: null as string | null
  });

  // Estado para controlar a aba ativa
  const [activeTab, setActiveTab] = useState('login-carousel');

  // Estado para template do contrato
  const [contractTemplate, setContractTemplate] = useState('');
  const [contractCoverImage, setContractCoverImage] = useState<string | null>(null);
  const [contractTemplateLoading, setContractTemplateLoading] = useState(false);
  const [contractTemplateSaving, setContractTemplateSaving] = useState(false);

  // Buscar imagens da API
  const { data: loginImages = [], isLoading } = useQuery<LoginImage[]>({
    queryKey: ['login-carousel-images'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/login-carousel/images/all`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Erro ao carregar imagens');
      const result = await response.json();
      return result.data;
    }
  });

  // Mutação para adicionar imagem
  const addImageMutation = useMutation({
    mutationFn: async (imageData: { url: string; alt: string }) => {
      const response = await fetch(`${API_BASE}/login-carousel/images`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(imageData)
      });
      if (!response.ok) throw new Error('Erro ao adicionar imagem');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['login-carousel-images'] });
      setNewImageUrl('');
      setNewImageAlt('');
      toast({
        title: "Sucesso",
        description: "Imagem adicionada ao carrossel."
      });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Erro ao adicionar imagem.",
        variant: "destructive"
      });
    }
  });

  // Mutação para remover imagem
  const removeImageMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(`/api/login-carousel/images/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Erro ao remover imagem');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['login-carousel-images'] });
      toast({
        title: "Sucesso",
        description: "Imagem removida do carrossel."
      });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Erro ao remover imagem.",
        variant: "destructive"
      });
    }
  });

  const handleAddImage = () => {
    if (!newImageUrl.trim()) {
      toast({
        title: "Erro",
        description: "URL da imagem é obrigatória.",
        variant: "destructive"
      });
      return;
    }

    addImageMutation.mutate({
      url: newImageUrl,
      alt: newImageAlt || 'Imagem do carrossel'
    });
  };

  const handleRemoveImage = (id: number) => {
    removeImageMutation.mutate(id);
  };

  // ===== FUNÇÕES PARA CONFIGURAÇÕES AVANÇADAS =====

  // Carregar configurações SMTP
  const loadSMTPSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/advanced-settings/smtp-alerts`, {
        credentials: 'include'
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSmtpSettings(result.data);
          setEmailInput(result.data.alertEmails.join(', '));
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configurações SMTP:', error);
    }
  };

  // Salvar configurações SMTP
  const saveSMTPSettings = async () => {
    setLoading(prev => ({ ...prev, smtp: true }));
    try {
      // Converter emailInput para array antes de enviar
      const emailsArray = emailInput.split(',').map(email => email.trim()).filter(Boolean);
      const settingsToSave = {
        ...smtpSettings,
        alertEmails: emailsArray
      };

      const response = await fetch(`${API_BASE}/advanced-settings/smtp-alerts`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(settingsToSave)
      });
      
      const result = await response.json();
      if (result.success) {
        toast({
          title: "Sucesso",
          description: result.message || "Configurações SMTP salvas com sucesso!",
        });
      } else {
        toast({
          title: "Erro",
          description: result.error || "Erro ao salvar configurações SMTP",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao salvar configurações SMTP",
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, smtp: false }));
    }
  };

  // Carregar configurações Dashboard
  const loadDashboardSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/advanced-settings/dashboard`, {
        credentials: 'include'
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setDashboardSettings(result.data);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configurações dashboard:', error);
    }
  };

  // Salvar configurações Dashboard
  const saveDashboardSettings = async () => {
    setLoading(prev => ({ ...prev, dashboard: true }));
    try {
      const response = await fetch(`${API_BASE}/advanced-settings/dashboard`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dashboardSettings)
      });
      
      const result = await response.json();
      if (result.success) {
        toast({
          title: "Sucesso",
          description: result.message || "Configurações do dashboard salvas com sucesso!",
        });
      } else {
        toast({
          title: "Erro",
          description: result.error || "Erro ao salvar configurações do dashboard",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao salvar configurações do dashboard",
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, dashboard: false }));
    }
  };

  // Carregar configurações Developer
  const loadDeveloperSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/advanced-settings/developer`, {
        credentials: 'include'
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setDeveloperSettings(result.data);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configurações developer:', error);
    }
  };

  // Salvar configurações Developer
  const saveDeveloperSettings = async () => {
    setLoading(prev => ({ ...prev, developer: true }));
    try {
      const response = await fetch(`${API_BASE}/advanced-settings/developer`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(developerSettings)
      });
      
      const result = await response.json();
      if (result.success) {
        toast({
          title: "Sucesso",
          description: result.message || "Configurações de developer salvas com sucesso!",
        });
      } else {
        toast({
          title: "Erro",
          description: result.error || "Erro ao salvar configurações de developer",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao salvar configurações de developer",
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, developer: false }));
    }
  };

  // Ações de controle
  const pauseAllLotes = async () => {
    setLoading(prev => ({ ...prev, actions: { ...prev.actions, pauseAll: true } }));
    try {
      const response = await fetch(`${API_BASE}/advanced-settings/developer/pause-all-lotes`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const result = await response.json();
      if (result.success) {
        toast({
          title: "Sucesso",
          description: result.message || "Lotes pausados com sucesso!",
        });
      } else {
        toast({
          title: "Erro",
          description: result.error || "Erro ao pausar lotes",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao pausar lotes",
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, actions: { ...prev.actions, pauseAll: false } }));
    }
  };

  const resumePausedLotes = async () => {
    setLoading(prev => ({ ...prev, actions: { ...prev.actions, resumeAll: true } }));
    try {
      const response = await fetch(`${API_BASE}/advanced-settings/developer/resume-paused-lotes`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const result = await response.json();
      if (result.success) {
        toast({
          title: "Sucesso",
          description: result.message || "Lotes retomados com sucesso!",
        });
      } else {
        toast({
          title: "Erro",
          description: result.error || "Erro ao retomar lotes",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao retomar lotes",
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, actions: { ...prev.actions, resumeAll: false } }));
    }
  };

  const clearRedis = async () => {
    setLoading(prev => ({ ...prev, actions: { ...prev.actions, clearRedis: true } }));
    try {
      const response = await fetch(`${API_BASE}/advanced-settings/developer/clear-redis`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const result = await response.json();
      if (result.success) {
        toast({
          title: "Sucesso",
          description: result.message || "Redis limpo com sucesso!",
        });
      } else {
        toast({
          title: "Erro",
          description: result.error || "Erro ao limpar Redis",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao limpar Redis",
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, actions: { ...prev.actions, clearRedis: false } }));
    }
  };

  // Carregar configurações Webhooks
  const loadWebhookSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/advanced-settings/webhooks`, {
        credentials: 'include'
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setWebhookSettings(result.data);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configurações webhook:', error);
    }
  };

  // Salvar configurações Webhooks
  const saveWebhookSettings = async () => {
    setLoading(prev => ({ ...prev, webhooks: true }));
    try {
      const response = await fetch(`${API_BASE}/advanced-settings/webhooks`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(webhookSettings)
      });
      const result = await response.json();
      if (result.success) {
        toast({ title: 'Sucesso', description: 'Configurações de webhook salvas com sucesso.' });
      } else {
        toast({ title: 'Erro', description: result.error || 'Erro ao salvar configurações.', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao salvar configurações de webhook.', variant: 'destructive' });
    } finally {
      setLoading(prev => ({ ...prev, webhooks: false }));
    }
  };

  // Testar webhook
  const testWebhook = async (event: string) => {
    setLoading(prev => ({ ...prev, webhookTestEvent: event }));
    try {
      const response = await fetch(`${API_BASE}/advanced-settings/webhooks/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ event })
      });
      const result = await response.json();
      if (result.success) {
        toast({ title: 'Sucesso', description: 'Webhook de teste enviado com sucesso.' });
      } else {
        toast({ title: 'Erro', description: result.error || 'Erro ao enviar webhook de teste.', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao enviar webhook de teste.', variant: 'destructive' });
    } finally {
      setLoading(prev => ({ ...prev, webhookTestEvent: null }));
    }
  };

  // Carregar configurações de espelhamento V2
  const loadAtomosV2MirrorSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/advanced-settings/atomos-v2-mirror`, {
        credentials: 'include'
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setAtomosV2MirrorSettings(result.data);
          setAtomosV2MirrorS3Info(result.s3 ?? null);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configurações de espelhamento V2:', error);
    }
  };

  const saveAtomosV2MirrorSettings = async (next: { enabled: boolean }) => {
    setLoading(prev => ({ ...prev, atomosV2Mirror: true }));
    try {
      const response = await fetch(`${API_BASE}/advanced-settings/atomos-v2-mirror`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(next)
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setAtomosV2MirrorSettings(result.data);
        toast({
          title: 'Sucesso',
          description: next.enabled
            ? 'Espelhamento de PDF V2 ativado.'
            : 'Espelhamento de PDF V2 desativado.'
        });
      } else {
        toast({ title: 'Erro', description: result.error || 'Erro ao salvar configuração.', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao salvar configuração de espelhamento V2.', variant: 'destructive' });
    } finally {
      setLoading(prev => ({ ...prev, atomosV2Mirror: false }));
    }
  };

  // Carregar template do contrato
  const loadContractTemplate = async () => {
    setContractTemplateLoading(true);
    try {
      const res = await fetch(`${API_BASE}/contracts/template`, { credentials: 'include' });
      if (!res.ok) throw new Error('Erro ao carregar template');
      const data = await res.json();
      if (data.success) {
        setContractTemplate(data.content || '');
        setContractCoverImage(data.coverImageBase64 || null);
      }
    } catch (error) {
      console.error('Erro ao carregar template do contrato:', error);
      toast({ title: 'Erro', description: 'Erro ao carregar template do contrato.', variant: 'destructive' });
    } finally {
      setContractTemplateLoading(false);
    }
  };

  const saveContractTemplate = async () => {
    setContractTemplateSaving(true);
    try {
      const res = await fetch(`${API_BASE}/contracts/template`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          content: contractTemplate,
          coverImageBase64: contractCoverImage,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar');
      toast({ title: 'Sucesso', description: 'Template do contrato salvo com sucesso.' });
    } catch (error) {
      toast({
        title: 'Erro',
        description: error instanceof Error ? error.message : 'Erro ao salvar template.',
        variant: 'destructive',
      });
    } finally {
      setContractTemplateSaving(false);
    }
  };

  // Carregar configurações ao montar o componente
  useEffect(() => {
    loadSMTPSettings();
    loadDashboardSettings();
    loadDeveloperSettings();
    loadWebhookSettings();
    loadAtomosV2MirrorSettings();
  }, []);

  // Carregar template do contrato quando a aba for ativada
  useEffect(() => {
    if (activeTab === 'contract-template') loadContractTemplate();
  }, [activeTab]);

  if (user?.role !== 'admin') {
    return (
      <RoleBasedNavigation>
        <Layout>
          <div className="min-h-screen flex items-center justify-center">
            <Card className="w-full max-w-md">
              <CardContent className="p-6 text-center">
                <p className="text-gray-600">Acesso negado. Apenas administradores podem acessar esta página.</p>
              </CardContent>
            </Card>
          </div>
        </Layout>
      </RoleBasedNavigation>
    );
  }

  return (
    <RoleBasedNavigation>
      <Layout>
        <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center">
          <SettingsIcon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-montserrat font-bold text-gray-900">Configurações</h1>
          <p className="text-gray-600">Gerencie as configurações do sistema</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Navegação minimalista */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('login-carousel')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'login-carousel'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Carrossel de Login
            </button>
            <button
              onClick={() => setActiveTab('smtp-alerts')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'smtp-alerts'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Alertas SMTP
            </button>
            <button
              onClick={() => setActiveTab('dashboard-monitoring')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'dashboard-monitoring'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Monitoramento
            </button>
            <button
              onClick={() => setActiveTab('developer-tools')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'developer-tools'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Developer Tools
            </button>
            <button
              onClick={() => setActiveTab('webhooks')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'webhooks'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Webhooks
            </button>
            <button
              onClick={() => setActiveTab('atomos-v2')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'atomos-v2'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              ÁtomosChat V2
            </button>
            <button
              onClick={() => setActiveTab('contract-template')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'contract-template'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Template de Contrato
            </button>
          </nav>
        </div>

        {/* Conteúdo das abas */}
        {activeTab === 'login-carousel' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <ImageIcon className="w-5 h-5" />
                <span>Carrossel de Imagens do Login</span>
              </CardTitle>
              <CardDescription>
                Configure as imagens que aparecem no carrossel da página de login
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Adicionar nova imagem */}
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">Adicionar Nova Imagem</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="imageUrl">URL da Imagem</Label>
                    <Input
                      id="imageUrl"
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                      placeholder="https://exemplo.com/imagem.jpg"
                    />
                  </div>
                  <div>
                    <Label htmlFor="imageAlt">Texto Alternativo</Label>
                    <Input
                      id="imageAlt"
                      value={newImageAlt}
                      onChange={(e) => setNewImageAlt(e.target.value)}
                      placeholder="Descrição da imagem"
                    />
                  </div>
                </div>
                <Button onClick={handleAddImage} className="w-full md:w-auto">
                  <Upload className="w-4 h-4 mr-2" />
                  Adicionar Imagem
                </Button>
              </div>

              {/* Lista de imagens */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Imagens do Carrossel ({loginImages.length})</h3>
                {loginImages.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <ImageIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>Nenhuma imagem configurada</p>
                    <p className="text-sm">Adicione imagens para criar o carrossel</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {loginImages
                      .sort((a, b) => a.order - b.order)
                      .map((image, index) => (
                        <div key={image.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
                          <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src={image.url}
                              alt={image.alt}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY2NjY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkVycm8gY2FycmVnYW5kbyBpbWFnZW08L3RleHQ+PC9zdmc+';
                              }}
                            />
                          </div>
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-gray-900 truncate">{image.alt}</p>
                            <p className="text-xs text-gray-500 truncate">{image.url}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-400">Ordem: {index + 1}</span>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleRemoveImage(image.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Aba Alertas SMTP */}
        {activeTab === 'smtp-alerts' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Mail className="w-5 h-5" />
                <span>Configurações de Alertas SMTP</span>
              </CardTitle>
              <CardDescription>
                Configure os alertas por e-mail do sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Habilitar/Desabilitar todos os alertas */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label htmlFor="enableAllAlerts" className="text-base font-medium">Habilitar Todos os Alertas</Label>
                  <p className="text-sm text-gray-600">Ativa ou desativa todos os alertas por e-mail</p>
                </div>
                <Switch
                  id="enableAllAlerts"
                  checked={smtpSettings.enableAllAlerts}
                  onCheckedChange={(checked) => setSmtpSettings(prev => ({ ...prev, enableAllAlerts: checked }))}
                />
              </div>

              {/* Tempo para considerar job parado */}
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">Tempo para Detectar Job Parado</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="jobStoppedTime">Tempo em segundos</Label>
                    <Input
                      id="jobStoppedTime"
                      type="number"
                      value={smtpSettings.jobStoppedTimeSeconds}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, jobStoppedTimeSeconds: parseInt(e.target.value) || 60 }))}
                      min="30"
                      max="3600"
                    />
                    <p className="text-xs text-gray-500 mt-1">Tempo em segundos para considerar um lote como parado</p>
                  </div>
                </div>
              </div>

              {/* E-mails para receber alertas */}
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">E-mails para Alertas</h3>
                <div>
                  <Label htmlFor="alertEmails">Lista de E-mails (separados por vírgula)</Label>
                  <Input
                    id="alertEmails"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="email1@exemplo.com, email2@exemplo.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">Separe múltiplos e-mails com vírgula</p>
                </div>
              </div>

              {/* Tipos de alertas */}
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">Tipos de Alertas</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="enableLoteCreated">Lote Criado</Label>
                    <Switch
                      id="enableLoteCreated"
                      checked={smtpSettings.enableLoteCreated}
                      onCheckedChange={(checked) => setSmtpSettings(prev => ({ ...prev, enableLoteCreated: checked }))}
                      disabled={!smtpSettings.enableAllAlerts}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="enableLoteStarted">Lote Iniciado</Label>
                    <Switch
                      id="enableLoteStarted"
                      checked={smtpSettings.enableLoteStarted}
                      onCheckedChange={(checked) => setSmtpSettings(prev => ({ ...prev, enableLoteStarted: checked }))}
                      disabled={!smtpSettings.enableAllAlerts}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="enableLoteCompleted">Lote Concluído</Label>
                    <Switch
                      id="enableLoteCompleted"
                      checked={smtpSettings.enableLoteCompleted}
                      onCheckedChange={(checked) => setSmtpSettings(prev => ({ ...prev, enableLoteCompleted: checked }))}
                      disabled={!smtpSettings.enableAllAlerts}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="enableLoteStopped">Lote Parado</Label>
                    <Switch
                      id="enableLoteStopped"
                      checked={smtpSettings.enableLoteStopped}
                      onCheckedChange={(checked) => setSmtpSettings(prev => ({ ...prev, enableLoteStopped: checked }))}
                      disabled={!smtpSettings.enableAllAlerts}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="enableLotePaused">Lote Pausado</Label>
                    <Switch
                      id="enableLotePaused"
                      checked={smtpSettings.enableLotePaused}
                      onCheckedChange={(checked) => setSmtpSettings(prev => ({ ...prev, enableLotePaused: checked }))}
                      disabled={!smtpSettings.enableAllAlerts}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="enableLoteResumed">Lote Retomado</Label>
                    <Switch
                      id="enableLoteResumed"
                      checked={smtpSettings.enableLoteResumed}
                      onCheckedChange={(checked) => setSmtpSettings(prev => ({ ...prev, enableLoteResumed: checked }))}
                      disabled={!smtpSettings.enableAllAlerts}
                    />
                  </div>
                </div>
              </div>

              <Button 
                className="w-full" 
                onClick={saveSMTPSettings}
                disabled={loading.smtp}
              >
                {loading.smtp ? 'Salvando...' : 'Salvar Configurações SMTP'}
              </Button>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Aba Monitoramento Dashboard */}
        {activeTab === 'dashboard-monitoring' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Monitor className="w-5 h-5" />
                <span>Monitoramento do Dashboard</span>
              </CardTitle>
              <CardDescription>
                Configure o tempo de atualização do dashboard
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">Intervalo de Atualização</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="refreshInterval">Tempo em segundos</Label>
                    <Select
                      value={dashboardSettings.refreshIntervalSeconds.toString()}
                      onValueChange={(value) => setDashboardSettings(prev => ({ ...prev, refreshIntervalSeconds: parseInt(value) }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o intervalo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 segundos</SelectItem>
                        <SelectItem value="60">1 minuto</SelectItem>
                        <SelectItem value="120">2 minutos</SelectItem>
                        <SelectItem value="300">5 minutos</SelectItem>
                        <SelectItem value="600">10 minutos</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">Frequência de atualização automática do dashboard</p>
                  </div>
                </div>
              </div>

              <Button 
                className="w-full" 
                onClick={saveDashboardSettings}
                disabled={loading.dashboard}
              >
                {loading.dashboard ? 'Salvando...' : 'Salvar Configurações do Dashboard'}
              </Button>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Aba Developer Tools */}
        {activeTab === 'developer-tools' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Wrench className="w-5 h-5" />
                <span>Ferramentas para Developers</span>
              </CardTitle>
              <CardDescription>
                Ferramentas avançadas para desenvolvimento e manutenção
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Ações de Controle */}
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">Controle de Lotes</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button 
                    variant="destructive" 
                    className="w-full"
                    onClick={pauseAllLotes}
                    disabled={loading.actions.pauseAll}
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    {loading.actions.pauseAll ? 'Pausando...' : 'Pausar Todos os Lotes'}
                  </Button>
                  <Button 
                    variant="default" 
                    className="w-full"
                    onClick={resumePausedLotes}
                    disabled={loading.actions.resumeAll}
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    {loading.actions.resumeAll ? 'Retomando...' : 'Iniciar Lotes Pausados'}
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={clearRedis}
                    disabled={loading.actions.clearRedis}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {loading.actions.clearRedis ? 'Limpando...' : 'Limpar Redis'}
                  </Button>
                </div>
              </div>

              {/* Configurações de Limpeza Redis */}
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">Limpeza Automática do Redis</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="redisCleanupInterval">Frequência de Limpeza</Label>
                    <Select
                      value={developerSettings.redisCleanupInterval}
                      onValueChange={(value) => setDeveloperSettings(prev => ({ ...prev, redisCleanupInterval: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a frequência" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">A cada hora</SelectItem>
                        <SelectItem value="daily">Diariamente</SelectItem>
                        <SelectItem value="fixed_time">Horário fixo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {developerSettings.redisCleanupInterval === 'fixed_time' && (
                    <div>
                      <Label htmlFor="redisCleanupTime">Horário de Limpeza</Label>
                      <Input
                        id="redisCleanupTime"
                        type="time"
                        value={developerSettings.redisCleanupTime}
                        onChange={(e) => setDeveloperSettings(prev => ({ ...prev, redisCleanupTime: e.target.value }))}
                      />
                      <p className="text-xs text-gray-500 mt-1">Horário para executar a limpeza automática</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  <h4 className="font-medium text-yellow-800">Atenção</h4>
                </div>
                <p className="text-sm text-yellow-700 mt-2">
                  Estas ferramentas são para uso avançado. Use com cuidado pois podem afetar o funcionamento do sistema.
                </p>
              </div>

              <Button 
                className="w-full" 
                onClick={saveDeveloperSettings}
                disabled={loading.developer}
              >
                {loading.developer ? 'Salvando...' : 'Salvar Configurações de Developer'}
              </Button>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Aba Webhooks */}
        {activeTab === 'webhooks' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Send className="w-5 h-5" />
                <span>Webhooks de Lotes</span>
              </CardTitle>
              <CardDescription>
                Configure webhooks para receber notificações de eventos de lotes (criado, pausado, concluído, etc.)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label htmlFor="webhookEnabled" className="text-base font-medium">Webhook Ativo</Label>
                  <p className="text-sm text-gray-600">Liga ou desliga o envio de webhooks</p>
                </div>
                <Switch
                  id="webhookEnabled"
                  checked={webhookSettings.enabled}
                  onCheckedChange={(checked) => setWebhookSettings(prev => ({ ...prev, enabled: checked }))}
                />
              </div>

              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <Label htmlFor="webhookUrl">URL do Webhook</Label>
                <Input
                  id="webhookUrl"
                  value={webhookSettings.webhookUrl}
                  onChange={(e) => setWebhookSettings(prev => ({ ...prev, webhookUrl: e.target.value }))}
                  placeholder="https://seu-servidor.com/webhook/lotes"
                />
                <p className="text-xs text-gray-500">Endereço que receberá os eventos em formato JSON</p>
              </div>

              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900">Eventos a enviar</h3>
                <p className="text-sm text-gray-600">Selecione quais eventos disparam o webhook e use Testar para enviar um payload de exemplo</p>
                <div className="space-y-3">
                  {[
                    { key: 'enableLoteCreated', label: 'Lote Criado', event: 'lote.criado' },
                    { key: 'enableLoteStarted', label: 'Lote Iniciado', event: 'lote.iniciado' },
                    { key: 'enableLotePaused', label: 'Lote Pausado', event: 'lote.pausado' },
                    { key: 'enableLoteResumed', label: 'Lote Retomado', event: 'lote.retomado' },
                    { key: 'enableLoteCancelled', label: 'Lote Cancelado', event: 'lote.cancelado' },
                    { key: 'enableLoteCompleted', label: 'Lote Concluído', event: 'lote.concluido' },
                    { key: 'enableLoteStopped', label: 'Lote Parado', event: 'lote.parado' }
                  ].map(({ key, label, event }) => (
                    <div key={key} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <Label htmlFor={key}>{label}</Label>
                      <div className="flex items-center gap-3">
                        <Switch
                          id={key}
                          checked={webhookSettings[key as keyof typeof webhookSettings] as boolean}
                          onCheckedChange={(checked) => setWebhookSettings(prev => ({ ...prev, [key]: checked }))}
                          disabled={!webhookSettings.enabled}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => testWebhook(event)}
                          disabled={!webhookSettings.webhookUrl?.trim() || loading.webhookTestEvent !== null}
                        >
                          {loading.webhookTestEvent === event ? 'Enviando...' : 'Testar'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Button 
                className="w-full" 
                onClick={saveWebhookSettings}
                disabled={loading.webhooks}
              >
                {loading.webhooks ? 'Salvando...' : 'Salvar Configurações de Webhook'}
              </Button>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Aba ÁtomosChat V2 — Espelhamento de PDF */}
        {activeTab === 'atomos-v2' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Send className="w-5 h-5" />
                  <span>ÁtomosChat V2 — Espelhamento de PDF no S3</span>
                </CardTitle>
                <CardDescription>
                  Quando ativo, antes de enviar um boleto via conexão V2 (ÁtomosChat) o sistema baixa o PDF da Hinova,
                  copia para o bucket S3 configurado e envia a URL pública desse bucket como <code className="text-xs">fileUrl</code> à API V2.
                  Útil para contornar falhas de SSL fetch da plataforma V2 ao baixar PDFs externos.
                  O caminho é fixo: <code className="text-xs">boletos/clientes/{'{client_id}'}/{'{lote_id}'}/boleto_{'{lote_id}'}_{'{boleto_id}'}.pdf</code>.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {atomosV2MirrorS3Info && !atomosV2MirrorS3Info.configured && (
                  <div className="flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      Bucket S3 do ÁtomosChat não configurado no servidor. Defina as variáveis
                      <code className="mx-1 text-xs">ATOMOS_S3_ENDPOINT</code>,
                      <code className="mx-1 text-xs">ATOMOS_S3_ACCESS_KEY</code>,
                      <code className="mx-1 text-xs">ATOMOS_S3_SECRET_KEY</code> e
                      <code className="mx-1 text-xs">ATOMOS_S3_BUCKET</code> antes de ativar.
                    </div>
                  </div>
                )}

                {atomosV2MirrorS3Info?.configured && (
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 space-y-1">
                    <div><span className="font-medium">Endpoint:</span> {atomosV2MirrorS3Info.endpoint}</div>
                    <div><span className="font-medium">Bucket:</span> {atomosV2MirrorS3Info.bucket}</div>
                    <div><span className="font-medium">Região:</span> {atomosV2MirrorS3Info.region}</div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">Espelhar PDF de boleto antes do envio V2</Label>
                    <p className="text-sm text-gray-600">
                      Tenta o espelhamento até 3 vezes; após 3 falhas, o boleto é marcado como FALHA no envio.
                    </p>
                  </div>
                  <Switch
                    checked={atomosV2MirrorSettings.enabled}
                    disabled={loading.atomosV2Mirror || (atomosV2MirrorS3Info ? !atomosV2MirrorS3Info.configured : false)}
                    onCheckedChange={(checked) => saveAtomosV2MirrorSettings({ enabled: checked })}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Aba Template de Contrato */}
        {activeTab === 'contract-template' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="w-5 h-5" />
                <span>Template do Contrato</span>
              </CardTitle>
              <CardDescription>
                Edite o template usado na geração de contratos em PDF. Adicione a imagem de capa e formate o texto. Use placeholders como {'{legal_name}'}, {'{company_tax_id}'}, {'{produtos}'}, {'{observacoes_gerais}'}.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {contractTemplateLoading ? (
                <div className="py-12 text-center text-gray-500">Carregando template...</div>
              ) : (
                <>
                  <ContractTemplateEditor
                    content={contractTemplate}
                    coverImageBase64={contractCoverImage}
                    onChangeContent={setContractTemplate}
                    onChangeCover={setContractCoverImage}
                    placeholder="Digite o conteúdo do contrato..."
                  />
                  <Button
                    onClick={saveContractTemplate}
                    disabled={contractTemplateSaving || (typeof contractTemplate !== 'string') || !contractTemplate.trim()}
                  >
                    {contractTemplateSaving ? 'Salvando...' : 'Salvar Template'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
          </div>
        )}
      </div>
        </div>
      </Layout>
    </RoleBasedNavigation>
  );
};

export default Settings;
